import './About.css';

export default function About() {
  return (
    <div className="about fade-in">
      {/* Hero Banner */}
      <section className="about-hero">
        <div className="container">
          <h1 className="section-title">👧 Emmi & Izzi's Story</h1>
          <p className="section-subtitle">
            Written by Emmi — age 8 💜
          </p>
        </div>
      </section>

      <div className="container about-content">

        {/* Emmi's Intro */}
        <div className="about-card card lavender-border">
          <div className="about-card-icon">🌸</div>
          <h2>Hi, I'm Emmi!</h2>
          <p>
            My name is Emmi and I am 8 years old. I made this website because
            I have a very special sister named Izzi. Izzi has something called
            <strong> lissencephaly</strong>, which means her brain is a little
            different from most people's brains. But different doesn't mean bad —
            it just means <em>special</em>!
          </p>
          <p>
            I made this website because I wanted other kids to understand what it's
            like to have a sibling (or friend!) with special needs. Sometimes kids
            don't know how to act around kids like Izzi, and that's okay — that's
            why I'm here to help! 🌟
          </p>
          <p>
            I love my sister more than anything, and I hope that after you read
            this, you will think kids like her are amazing too.
          </p>
        </div>

        {/* Photo */}
        <div className="photo-placeholder card">
          <div className="photo-frame">
            <img
              src="/Emmi-and-Izzi.jpg"
              alt="Emmi and her sister Izzi smiling together"
              className="sisters-photo"
              onError={e => {
                // Falls back to placeholder if photo not added yet
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="photo-mock" style={{ display: 'none' }}>
              <span className="photo-emoji">📸</span>
              <p>A photo of Emmi and Izzi will go here!</p>
              <small>Two sisters, one big love 💜</small>
            </div>
          </div>
          <div className="photo-caption">
            <strong>Emmi & Izzi</strong> — best sisters forever 💜
          </div>
        </div>

        {/* About Izzi */}
        <div className="about-card card mint-border">
          <div className="about-card-icon">💚</div>
          <h2>Meet My Sister Izzi! 🌟</h2>
          <p>
            My sister Izzi is honestly one of the funniest people I know. She has
            the BIGGEST smile and she loves music SO much. When a song she likes
            comes on, she lights up like a Christmas tree — it's the best thing
            ever!
          </p>
          <p>
            Izzi has a rare brain condition called <strong>lissencephaly</strong>.
            It means that when her brain was growing before she was born, it didn't
            form the usual bumps and folds. Because of this, some things are harder
            for Izzi than for most kids — like talking, moving around, and doing
            things on her own.
          </p>
          <p>
            But here's what's really cool: Izzi has her <strong>own amazing ways
            of talking to us</strong>! She uses sign language 🤟 and a special
            tablet called an <strong>AAC device</strong> (that stands for
            Augmentative and Alternative Communication — it's a mouth in a box!).
            She can also say a few words on her own, and every single one of them
            makes our whole family SO happy.
          </p>
          <p>
            Izzi might not do everything the same way as other kids, but she is
            <strong> full of personality, love, and joy</strong>. She notices
            everything, she knows when you're happy or sad, and she will absolutely
            steal your heart. 💜
          </p>
        </div>

        {/* What is Lissencephaly */}
        <div className="about-card card peach-border">
          <div className="about-card-icon">🧠</div>
          <h2>What is Lissencephaly?</h2>
          <p>
            The word "lissencephaly" means <em>"smooth brain"</em> in Greek. Our
            brains are supposed to have lots of wrinkles and folds — those folds
            help our brain cells connect to each other. But kids with lissencephaly
            have a brain that is much smoother than usual, which means some
            connections that most people have don't form the same way.
          </p>
          <p>
            It's a rare condition, which means not a lot of people have it. And
            every kid with lissencephaly is different — some can do more things,
            some need more help. But all of them are <strong>amazing, valuable,
            and wonderful people</strong>.
          </p>
          <div className="liss-facts">
            <div className="liss-fact">
              <span>🌍</span>
              <p>Lissencephaly affects about 1 in 100,000 babies</p>
            </div>
            <div className="liss-fact">
              <span>❤️</span>
              <p>Kids with lissencephaly can feel love, joy, and happiness</p>
            </div>
            <div className="liss-fact">
              <span>🗣️</span>
              <p>Many communicate using sign language, devices, or expressions</p>
            </div>
          </div>
        </div>

        {/* How to be a friend to Izzi */}
        <div className="about-card card blue-border">
          <div className="about-card-icon">🤝</div>
          <h2>How to Be a Great Friend to Someone Like Izzi</h2>
          <p className="tip-intro">Here's what I've learned from being Izzi's sister! 💜</p>
          <ul className="friend-tips">
            <li>
              <span className="tip-icon">👀</span>
              <div>
                <strong>Look at them, not away.</strong> It can feel weird at first
                to meet someone who moves or communicates differently. But looking
                at them and smiling makes them feel seen and included!
              </div>
            </li>
            <li>
              <span className="tip-icon">🗣️</span>
              <div>
                <strong>Talk TO them, not just about them.</strong> Even if they
                can't answer in words, they can still hear you and understand more
                than you think. Say hi! Tell them about your day. They love it!
              </div>
            </li>
            <li>
              <span className="tip-icon">⏳</span>
              <div>
                <strong>Be patient.</strong> It might take longer to communicate
                with someone who uses a device or sign language. That's okay!
                Good friendships are worth waiting for.
              </div>
            </li>
            <li>
              <span className="tip-icon">🎵</span>
              <div>
                <strong>Do fun things together.</strong> Listen to music, watch a
                movie, blow bubbles — there are so many things everyone can enjoy
                together no matter what!
              </div>
            </li>
            <li>
              <span className="tip-icon">🙋</span>
              <div>
                <strong>Ask how to help.</strong> Instead of guessing, ask a parent
                or the person themselves the best way to help or include them.
              </div>
            </li>
          </ul>
        </div>

        {/* Closing message */}
        <div className="about-closing">
          <div className="closing-content">
            <span className="closing-emoji">💜</span>
            <h3>From Emmi, with love</h3>
            <p>
              "I made this website because I want everyone to know how incredible
              kids with special needs are. My sister Izzi has taught me more about
              love, patience, and what really matters than anyone else in the world.
              I hope you'll learn something here that helps you be a better friend
              to someone who is special and beyond. 🌟"
            </p>
            <div className="closing-hearts">💜 💚 🍑 💙</div>
          </div>
        </div>

      </div>
    </div>
  );
}
